import { useLazyQuery } from '@apollo/client';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { Security, useOktaAuth } from '@okta/okta-react';
import type { RestoreOriginalUriFunction } from '@okta/okta-react/bundles/types/OktaContext';
import { includes, replace, toLower } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import type { Dispatch, ReactElement, ReactNode, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { RootState } from 'store';

import { AuthActions } from 'common/auth/actions';
import { isUserLoggedIn } from 'common/auth/auth_role_permissions_utils';
import { HealthCheckBackendQuery } from 'common/auth/queries';
import { selectIsDeviceHandoffInProgress } from 'common/deviceHandoff/state/selectors';
import { CIError } from 'common/error/error_util';
import { AuthUserType } from 'common/graphql/types';
import { useClient } from 'common/hooks/use-client';
import { useOnVisibilityChange } from 'common/hooks/useOnVisibilityChange';
import { CILogger } from 'common/log/log.provider';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';
import { saveLastPagesOpenedAndLogout, TimeoutConfigCognito, TimeoutConfigOkta } from 'common/viewcomp/layout/helpers';

import oktaAuth from './oktaAuth';
import {
    OKTA_SESSION_CHECK_SHARED_WINDOW_MS,
    runCoordinatedOktaSessionCheck,
} from './utils/coordinatedOktaSessionCheck';
import { fetchOktaSession } from './utils/fetchOktaSession';
import { guardedOktaSignInWithRedirect } from './utils/oktaUtils';

/** Keep-alive interval while no expiry is known yet, e.g. when the first check got no usable answer from Okta. */
const UNKNOWN_EXPIRY_KEEP_ALIVE_INTERVAL_MS = 5 * 60 * 1000;

const getKeepAliveInterval = (sessionExpiration: Date | null) => {
    if (!sessionExpiration) {
        return UNKNOWN_EXPIRY_KEEP_ALIVE_INTERVAL_MS;
    }

    // Never shorter than the shared window: close to or past the expiry, half the remaining time shrinks to nothing
    // and would check in a tight loop, asking Okta on every tick while it gives no usable answer.
    return Math.max((sessionExpiration.getTime() - Date.now()) / 2, OKTA_SESSION_CHECK_SHARED_WINDOW_MS);
};

const isUserCurrentlyActive = (inactivityThresholdSec: number) => {
    const lastActivity = +(sessionStorage.getItem(TimeoutConfigCognito.storageKey) || 0);
    const now = Math.floor(Date.now() / 1000);

    return now - lastActivity < inactivityThresholdSec;
};

interface Props {
    children?: ReactNode;
    /**
     * Whether the current route may probe Okta for an existing session to auto sign-in a user who is not logged
     * in. Public routes (e.g. headless export pages) leave this off so anonymous loads never call Okta.
     */
    allowAutoSignIn?: boolean;
}

const AuthStateMonitor = ({ children, allowAutoSignIn = false }: Props): ReactElement => {
    const { oktaAuth: oktaAuthInstance } = useOktaAuth();
    const auth = useSelector((state: RootState) => state.auth);
    const { t } = useTranslation('auth');
    const dispatch = useDispatch();
    const [healthCheckBackend] = useLazyQuery(HealthCheckBackendQuery);
    const [sessionExpiration, setSessionExpiration] = useState<Date | null>(null);
    const inactivityThreshold = TimeoutConfigOkta.totalSessionDuration - TimeoutConfigCognito.dialogDuration; // matches the time when SessionTimeoutDialog shows the warning dialog

    const isOktaLogin =
        auth?.activeUser?.userType !== AuthUserType.PATIENT && auth?.activeUser?.authenticationMethod === 'OKTA';

    const { checkOktaSession, checkIfOktaSessionExistsAndSignOutIfNot } = useCheckOktaSession(
        setSessionExpiration,
        isOktaLogin,
        allowAutoSignIn
    );

    const backendHealthCheck = useCallback(async () => {
        if (!isOktaLogin || !isUserLoggedIn(auth)) {
            return;
        }

        // TOOD: This should be handled either by okta tokens or by comparing session ids instead of only user name in checkIfOktaSessionExistsAndSignOutIfNot (isSameUser)
        const { error } = await healthCheckBackend();

        if (error) {
            const ciError = CIError.fromGraphQLError(error);
            if (ciError?.translationKey === 'network.401error') {
                const oktaSessionExists = await oktaAuthInstance.session.exists();
                if (oktaSessionExists) {
                    CIPageStatus.error(t('login.sso.error.session_refreshing'));

                    oktaAuthInstance.tokenManager.clear();
                    dispatch(AuthActions.logoutRequest());
                    await guardedOktaSignInWithRedirect(oktaAuthInstance);
                }
            } else {
                CIPageStatus.error(t(ciError?.translationKey));
                CILogger.getInstance().info('Unexpected error during backend health check: ', error);
            }
        }
    }, [isOktaLogin, auth, oktaAuthInstance, dispatch]);

    useEffect(
        function verifyOktaSessionOnpageReload() {
            backendHealthCheck();
            checkOktaSession();
        },
        [] // there is no dependency during signing out of user, it is meant only for page reload
    );

    useOnVisibilityChange(checkOktaSession);

    useEffect(
        function refreshSession() {
            if (!isOktaLogin || !isUserLoggedIn(auth)) {
                return;
            }

            const keepAliveInterval = getKeepAliveInterval(sessionExpiration);

            const intervalId = setInterval(() => {
                const userIsActive = isUserCurrentlyActive(inactivityThreshold);
                if (userIsActive && document.visibilityState === 'visible') {
                    checkIfOktaSessionExistsAndSignOutIfNot();
                }
            }, keepAliveInterval);

            return () => clearInterval(intervalId);
        },
        [isOktaLogin, auth, checkIfOktaSessionExistsAndSignOutIfNot, sessionExpiration, inactivityThreshold]
    );

    useEffect(
        function initTokenManagerHandlers() {
            if (!isOktaLogin) {
                return;
            }

            const handleTokenError = (error: any) => {
                CILogger.getInstance().error('Token renewal failed:', error);
                checkIfOktaSessionExistsAndSignOutIfNot();
            };

            const handleTokenRenewed = (key: string) => {
                CILogger.getInstance().info(`Token renewed: ${key}`);
            };

            oktaAuthInstance.tokenManager.on('error', handleTokenError);
            oktaAuthInstance.tokenManager.on('renewed', handleTokenRenewed);

            return () => {
                oktaAuthInstance.tokenManager.off('error', handleTokenError);
                oktaAuthInstance.tokenManager.off('renewed', handleTokenRenewed);
            };
        },
        [isOktaLogin, oktaAuthInstance, dispatch, checkIfOktaSessionExistsAndSignOutIfNot]
    );

    return <>{children}</>;
};

const useCheckOktaSession = (
    setSessionExpiration: Dispatch<SetStateAction<Date | null>>,
    isOktaLogin: boolean,
    allowAutoSignIn: boolean
) => {
    const { oktaAuth: oktaAuthInstance } = useOktaAuth();
    const auth = useSelector((state: RootState) => state.auth);
    const client = useClient();
    const { t } = useTranslation('auth');
    const dispatch = useDispatch();

    const handleLogout = useCallback(() => {
        dispatch(AuthActions.logoutRequest({ endOktaSessionLocally: true }));
    }, [dispatch]);

    const isDeviceHandoffInProgress = useSelector(selectIsDeviceHandoffInProgress);

    const checkIfOktaSessionExistsAndSignOutIfNot = useCallback(async () => {
        if (includes(window.location.pathname, '/sso/okta') || isDeviceHandoffInProgress) {
            return;
        }

        const { result: sessionInfo, ranCheck } = await runCoordinatedOktaSessionCheck(() =>
            fetchOktaSession(oktaAuthInstance)
        );

        if (sessionInfo.expiresAt) {
            const expiration = new Date(sessionInfo.expiresAt);

            // Keeps the same Date when nothing changed, so a reused answer does not restart the keep-alive timer.
            setSessionExpiration((current) => (current?.getTime() === expiration.getTime() ? current : expiration));
        }

        // The call that asked Okta acts on the answer; other tabs follow its logout through the shared activeUser.
        if (!ranCheck) {
            return;
        }

        if (sessionInfo.outcome === 'ambiguous') {
            CILogger.getInstance().warn(
                `Okta session check got no usable answer (HTTP status: ${
                    sessionInfo.httpStatus ?? 'none'
                }), keeping the user signed in until the next check.`
            );

            return;
        }

        // Acted on even when this tab is hidden: other tabs reuse this answer without acting on it, so dropping it here
        // would leave every tab signed in. A request cut off by leaving the page comes back ambiguous, not inactive.
        if (sessionInfo.outcome === 'inactive') {
            CIPageStatus.error(t('login.sso.error.session_terminated'));
            CILogger.getInstance().info('User signed out because invalid okta session detected.');

            saveLastPagesOpenedAndLogout(auth, client, handleLogout);
        } else {
            const oktaLogin = sessionInfo.login;
            const activeUserName = replace(auth?.activeUser?.name || '', /^FX_/, '');
            const isSameUser = toLower(oktaLogin) === toLower(activeUserName);

            // different user detected in okta, logout and sign in with new user
            if (!isSameUser && auth?.activeUser?.name) {
                CIPageStatus.error(t('login.sso.error.session_changed'));
                CILogger.getInstance().info(
                    `Different user detected in okta, logging out user ${auth.activeUser.pk} and signing in the Okta user.`
                );

                saveLastPagesOpenedAndLogout(auth, client, handleLogout);
                await guardedOktaSignInWithRedirect(oktaAuthInstance);
            }
        }
    }, [auth, client, oktaAuthInstance, dispatch, handleLogout, isDeviceHandoffInProgress]);

    const checkIfOktaSessionExistsAndSignInIfYes = useCallback(async () => {
        if (
            !allowAutoSignIn ||
            isUserLoggedIn(auth) ||
            includes(window.location.pathname, '/sso/okta') ||
            isDeviceHandoffInProgress
        ) {
            return;
        }

        try {
            dispatch(AuthActions.setSSOLoading(true));

            const oktaSessionExists = await oktaAuthInstance.session.exists();

            if (oktaSessionExists) {
                await guardedOktaSignInWithRedirect(oktaAuthInstance);
            } else {
                dispatch(AuthActions.setSSOLoading(false));
            }
        } catch (e) {
            dispatch(AuthActions.setSSOLoading(false));
            CILogger.getInstance().info('No SSO session detected', e);
        }
    }, [dispatch, oktaAuthInstance, auth, isDeviceHandoffInProgress, allowAutoSignIn]);

    const checkOktaSession = useCallback(() => {
        if (isOktaLogin) {
            void checkIfOktaSessionExistsAndSignOutIfNot();
        } else {
            void checkIfOktaSessionExistsAndSignInIfYes();
        }
    }, [isOktaLogin, checkIfOktaSessionExistsAndSignInIfYes, checkIfOktaSessionExistsAndSignOutIfNot]);

    return { checkOktaSession, checkIfOktaSessionExistsAndSignOutIfNot };
};

const OktaAuthProvider = ({ children, allowAutoSignIn }: Props): ReactElement => {
    const navigate = useNavigate();

    const restoreOriginalUri: RestoreOriginalUriFunction = (_oktaAuth, originalUri) => {
        navigate(toRelativeUrl(originalUri || '/', window.location.origin), { replace: true });
    };

    return (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <AuthStateMonitor allowAutoSignIn={allowAutoSignIn}>{children}</AuthStateMonitor>
        </Security>
    );
};

export default OktaAuthProvider;
